import { mdiAlert, mdiCheckBold, mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import toastAtom from '@/stores/atoms/toastAtom';

export const toastTypes = {
  success: 'success',
  warning: 'warning',
  error: 'error',
} as const;

export type ToastProps = {
  text: string;
  isOpen: boolean;
  type: (typeof toastTypes)[keyof typeof toastTypes];
};

export default function Toast({ text, isOpen, type }: ToastProps) {
  const [toast, setToast] = useAtom(toastAtom);
  const bgColor = type === toastTypes.success ? 'bg-success' : type === toastTypes.warning ? 'bg-primary' : 'bg-error';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setToast({ ...toast, isOpen: false });
  };

  useEffect(() => {
    if (isOpen) {
      const timerId = setTimeout(() => {
        setToast({ ...toast, isOpen: false });
      }, 5000);

      return () => clearTimeout(timerId);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id="toast-default"
          className={`${bgColor} fixed right-4 top-10 z-[10000] flex w-full max-w-xs items-center rounded-lg p-4 text-white`}
          role="alert"
          initial={{ x: '100%' }}
          animate={{ x: '0%' }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.25, ease: 'circInOut' }}
          onClick={e => e.stopPropagation()}
        >
          {/* 折り返しテキストでもアイコンが潰れないように shrink-0 を付与 */}
          <div className="size-5 shrink-0">
            <Icon path={type === 'success' ? mdiCheckBold : mdiAlert} size={0.8} />
          </div>
          <div className="ml-3 min-w-0 flex-1 break-words text-sm font-normal">{text}</div>
          <button
            type="button"
            className="-m-4 ml-2 inline-flex size-10 shrink-0 items-center justify-center rounded-lg p-1.5 text-white"
            data-dismiss-target="#toast-default"
            aria-label="Close"
            onClick={handleClick}
          >
            <Icon path={mdiClose} size={0.8} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
